import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Truck, MapPin, Clock, ShieldCheck, AlertTriangle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function fmtUsd(cents) {
  if (cents === null || cents === undefined) return "—";
  return `$${(cents / 100).toFixed(2)}`;
}

export default function SameDay() {
  const [zip, setZip] = useState("");
  const [eligibility, setEligibility] = useState(null);
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [cart, setCart] = useState([]); // [{product_id,title,unit_price_cents,qty,image}]
  const [feeQuote, setFeeQuote] = useState(null);
  const [checkingZip, setCheckingZip] = useState(false);

  useEffect(() => {
    (async () => {
      setLoadingProducts(true);
      try {
        const r = await fetch(`${API}/api/products/same-day-eligible`);
        const data = await r.json();
        setProducts(data.items || []);
      } catch {
        setProducts([]);
      } finally {
        setLoadingProducts(false);
      }
    })();
  }, []);

  const subtotalCents = cart.reduce(
    (s, it) => s + (it.unit_price_cents || 0) * (it.qty || 1),
    0
  );

  useEffect(() => {
    if (!eligibility?.eligible || subtotalCents === 0) {
      setFeeQuote(null);
      return;
    }
    (async () => {
      try {
        const r = await fetch(`${API}/api/orders/estimate-fee`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            zip_code: eligibility.zip,
            subtotal_cents: subtotalCents,
            miles: 1.0,
          }),
        });
        setFeeQuote(await r.json());
      } catch {
        setFeeQuote(null);
      }
    })();
  }, [eligibility, subtotalCents]);

  async function checkZip() {
    if (!zip || zip.length < 5) return;
    setCheckingZip(true);
    try {
      const r = await fetch(`${API}/api/same-day/check-location`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ zip_code: zip }),
      });
      setEligibility(await r.json());
    } catch {
      setEligibility({ eligible: false, reason: "Network error. Try again." });
    } finally {
      setCheckingZip(false);
    }
  }

  function addToCart(p) {
    setCart((c) => {
      const existing = c.find((x) => x.product_id === p.product_id);
      if (existing) {
        return c.map((x) =>
          x.product_id === p.product_id ? { ...x, qty: x.qty + 1 } : x
        );
      }
      return [
        ...c,
        {
          product_id: p.product_id,
          title: p.title,
          unit_price_cents: p.price_cents,
          image: p.image,
          qty: 1,
        },
      ];
    });
  }

  return (
    <div className="container mx-auto px-4 py-8 lg:py-14 max-w-6xl" data-testid="same-day-page">
      <section className="glass-panel p-6 lg:p-10 mb-8">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-lifted-green mb-3">
          <Truck size={14} /> Same-Day Delivery
        </div>
        <h1 className="font-anton text-4xl sm:text-5xl lg:text-6xl uppercase leading-[0.95]">
          Order it now.<br />
          <span className="text-lifted-green">We deliver today.</span>
        </h1>
        <p className="text-lifted-dim text-base sm:text-lg mt-5 max-w-2xl">
          Lifted Supplies runs a local same-day delivery network powered by our partner
          stores and driver community. Drop your ZIP to see if you're inside the service area.
        </p>

        <div className="mt-6 flex flex-col sm:flex-row gap-3 max-w-md" data-testid="zip-check-form">
          <input
            type="text"
            inputMode="numeric"
            maxLength={5}
            placeholder="Your ZIP code"
            value={zip}
            onChange={(e) => setZip(e.target.value.replace(/\D/g, ""))}
            className="flex-1 px-4 py-3 rounded-xl bg-black/40 border border-white/15 text-white placeholder:text-white/30 focus:outline-none focus:border-lifted-green"
            data-testid="zip-input"
          />
          <button
            onClick={checkZip}
            disabled={checkingZip || zip.length < 5}
            className="btn-lifted disabled:opacity-50"
            data-testid="check-zip-btn"
          >
            {checkingZip ? "Checking…" : "Check"}
          </button>
        </div>

        {eligibility && (
          <div
            className={`mt-5 p-4 rounded-xl border ${
              eligibility.eligible
                ? "bg-lifted-green/10 border-lifted-green/30 text-white"
                : "bg-red-900/20 border-red-500/30 text-red-100"
            }`}
            data-testid="zip-result"
          >
            {eligibility.eligible ? (
              <div className="flex items-start gap-3">
                <Clock size={18} className="mt-1 text-lifted-green" />
                <div>
                  <p className="font-bebas tracking-wider text-lg">
                    Yes — you're in the zone.
                  </p>
                  <p className="text-sm text-lifted-dim">
                    Estimated delivery: {eligibility.eta_min_minutes}–
                    {eligibility.eta_max_minutes} min from order confirmation.
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-3">
                <AlertTriangle size={18} className="mt-1" />
                <div>
                  <p className="font-bebas tracking-wider text-lg">
                    Not yet covered.
                  </p>
                  <p className="text-sm opacity-90">{eligibility.reason}</p>
                  <Link to="/shop" className="text-lifted-green underline text-sm mt-2 inline-block">
                    Continue with standard shipping →
                  </Link>
                </div>
              </div>
            )}
          </div>
        )}
      </section>

      {/* Product browse */}
      <section className="mb-10">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-anton uppercase text-2xl text-white">Same-day catalog</h2>
          <span className="text-xs text-lifted-dim">{products.length} item(s)</span>
        </div>
        {loadingProducts ? (
          <p className="text-lifted-dim">Loading…</p>
        ) : products.length === 0 ? (
          <div className="glass-panel p-6">
            <p className="text-lifted-dim">
              No items are flagged for same-day delivery yet. Admin can add same-day
              inventory in <code className="text-lifted-green">/admin/supplies/inventory</code>.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {products.map((p) => (
              <div key={p.product_id} className="glass-panel p-3" data-testid={`sameday-card-${p.product_id}`}>
                <div className="aspect-square bg-black overflow-hidden rounded-lg">
                  {p.image && (
                    <img src={p.image} alt={p.title} className="w-full h-full object-cover" />
                  )}
                </div>
                <p className="font-bebas tracking-wider text-white mt-2 text-sm">{p.title}</p>
                <p className="text-lifted-green text-sm mb-2">{fmtUsd(p.price_cents)}</p>
                <button
                  onClick={() => addToCart(p)}
                  className="w-full text-xs uppercase tracking-wider py-2 rounded-md bg-lifted-green/15 text-lifted-green hover:bg-lifted-green/25"
                  data-testid={`add-to-cart-${p.product_id}`}
                >
                  Add to cart
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Cart preview */}
      {cart.length > 0 && (
        <section className="glass-panel p-6 mb-8" data-testid="same-day-cart-preview">
          <h2 className="font-anton uppercase text-xl text-white mb-3">Your cart</h2>
          <ul className="divide-y divide-white/10 mb-4">
            {cart.map((it) => (
              <li key={it.product_id} className="py-2 flex justify-between text-sm">
                <span className="text-white">
                  {it.title} × {it.qty}
                </span>
                <span className="text-lifted-dim">
                  {fmtUsd(it.unit_price_cents * it.qty)}
                </span>
              </li>
            ))}
          </ul>
          <div className="text-sm text-lifted-dim space-y-1">
            <div className="flex justify-between">
              <span>Subtotal</span>
              <span>{fmtUsd(subtotalCents)}</span>
            </div>
            <div className="flex justify-between">
              <span>Delivery fee</span>
              <span>
                {feeQuote?.connection_required
                  ? "Awaiting fee config"
                  : fmtUsd(feeQuote?.delivery_fee_cents)}
              </span>
            </div>
            <div className="flex justify-between">
              <span>Service fee</span>
              <span>
                {feeQuote?.connection_required
                  ? "—"
                  : fmtUsd(feeQuote?.service_fee_cents)}
              </span>
            </div>
            <div className="flex justify-between text-white font-bebas tracking-wider pt-2 border-t border-white/10 mt-2">
              <span>Total</span>
              <span>{fmtUsd(feeQuote?.total_cents ?? subtotalCents)}</span>
            </div>
          </div>
          <Link
            to={
              eligibility?.eligible
                ? `/track?prefill=1`
                : "#"
            }
            className="btn-lifted mt-5 inline-flex"
            onClick={(e) => {
              if (!eligibility?.eligible) {
                e.preventDefault();
              } else {
                // store cart + zip so Track / Checkout can read it later
                try {
                  localStorage.setItem(
                    "lsd_cart",
                    JSON.stringify({ cart, zip: eligibility.zip, eta: eligibility })
                  );
                } catch (_e) {
                  /* ignore */
                }
              }
            }}
            data-testid="proceed-checkout-btn"
          >
            Continue to checkout
          </Link>
        </section>
      )}

      {/* Trust strip */}
      <section className="grid sm:grid-cols-3 gap-3 mt-8">
        <div className="glass-panel p-4 flex items-start gap-3">
          <ShieldCheck size={18} className="text-lifted-green mt-1" />
          <div>
            <p className="font-bebas tracking-wider text-white">21+ verified</p>
            <p className="text-xs text-lifted-dim">
              Every order requires age confirmation at checkout.
            </p>
          </div>
        </div>
        <div className="glass-panel p-4 flex items-start gap-3">
          <MapPin size={18} className="text-lifted-green mt-1" />
          <div>
            <p className="font-bebas tracking-wider text-white">Local pickup</p>
            <p className="text-xs text-lifted-dim">
              We dispatch from Lifted partner stores in your zone.
            </p>
          </div>
        </div>
        <div className="glass-panel p-4 flex items-start gap-3">
          <Truck size={18} className="text-lifted-green mt-1" />
          <div>
            <p className="font-bebas tracking-wider text-white">Track live</p>
            <p className="text-xs text-lifted-dim">
              Real-time timeline after the driver picks up your order.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
